import { authorizedFetch, apiCall } from '../utils/api';
import browserCacheService from './BrowserCacheService';
import { onAuthReset } from '../utils/auth';

// 🔥 PERFORMANCE: In-Memory Cache für Connection-Status (5 Minuten TTL)
const connectionStatusCache = new Map();
const CACHE_TTL = 5 * 60 * 1000; // 5 Minuten
// Status gilt aus Sicht des angemeldeten Kontos: bei Logout/Nutzerwechsel verwerfen.
onAuthReset(() => connectionStatusCache.clear());

/**
 * Service für Chat-Funktionalitäten
 */
const ChatService = {
  /**
   * Sendet eine Chat-Anfrage an einen anderen Benutzer
   * @param {string} recipientId - ID des Empfängers
   * @param {string} message - Optionale Nachricht mit der Anfrage
   * @returns {Promise<Object>} - Ergebnis der Anfrage
   */
  // origin (optional, Okt 2026): aus welcher Ansicht die Anfrage kommt
  // (ROUND | SEARCH | SKILL_BROWSE | PROFILE_LINK | OTHER), fuer den
  // Forschungsexport (Codebuch: Herkunft von Vernetzungen). Ohne Wert wird das
  // Feld gar nicht mitgeschickt; der Server speichert dann null.
  sendChatRequest: async (recipientId, message = '', origin) => {
    try {
      const payload = {
        recipientId,
        message: message || 'Ich würde gerne mit dir lernen! Können wir uns connecten?'
      };
      if (typeof origin === 'string' && origin) payload.origin = origin;
      const response = await apiCall('/api/v1/chat/request', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      // 🔥 PERFORMANCE: Invalidate cache after sending request
      connectionStatusCache.delete(recipientId);

      return response;
    } catch (error) {
      console.error('Fehler beim Senden der Chat-Anfrage:', error);
      throw error;
    }
  },

  /**
   * Holt alle aktiven Chat-Anfragen für den Benutzer
   * @returns {Promise<Array>} - Liste der Chat-Anfragen
   */
  getChatRequests: async () => {
    try {
      const response = await apiCall('/api/v1/chat/requests');
      return response;
    } catch (error) {
      console.error('Fehler beim Laden der Chat-Anfragen:', error);
      throw error;
    }
  },

  /**
   * Akzeptiert eine Chat-Anfrage
   * @param {string} requestId - ID der Chat-Anfrage
   * @returns {Promise<Object>} - Ergebnis der Aktion
   */
  acceptChatRequest: async (requestId) => {
    try {
      const response = await apiCall(`/api/v1/chat/request/${requestId}/accept`, {
        method: 'POST'
      });

      // 🆕 Cache invalidieren nach erfolgreichem Accept (22. Jan 2026)
      browserCacheService.invalidateConnections();
      // 🔥 PERFORMANCE: Clear connection status cache
      connectionStatusCache.clear();

      return response;
    } catch (error) {
      console.error('Fehler beim Akzeptieren der Chat-Anfrage:', error);
      throw error;
    }
  },

  /**
   * Lehnt eine Chat-Anfrage ab
   * @param {string} requestId - ID der Chat-Anfrage
   * @returns {Promise<Object>} - Ergebnis der Aktion
   */
  declineChatRequest: async (requestId) => {
    try {
      const response = await apiCall(`/api/v1/chat/request/${requestId}/decline`, {
        method: 'POST'
      });
      return response;
    } catch (error) {
      console.error('Fehler beim Ablehnen der Chat-Anfrage:', error);
      throw error;
    }
  },

  /**
   * Lädt alle Chat-Konversationen des Benutzers
   * @returns {Promise<Array>} - Liste der Chat-Konversationen
   */
  getConversations: async () => {
    try {
      const response = await apiCall('/api/v1/chat/conversations');
      return response;
    } catch (error) {
      console.error('Fehler beim Laden der Chat-Konversationen:', error);
      throw error;
    }
  },

  /**
   * Lädt die neuesten Nachrichten einer Konversation (eine Seite, Server-Default 50)
   * @param {string} conversationId - ID der Konversation
   * @param {Object} [options]
   * @param {string} [options.before] - ID der ältesten geladenen Nachricht ("Ältere laden")
   * @returns {Promise<Object>} - { success, messages (neueste zuerst), hasMore, partnerLastReadAt }
   */
  getMessages: async (conversationId, { before } = {}) => {
    try {
      const query = before ? `?before=${encodeURIComponent(before)}` : '';
      const response = await apiCall(`/api/v1/chat/conversation/${conversationId}/messages${query}`);
      return response;
    } catch (error) {
      console.error('Fehler beim Laden der Nachrichten:', error);
      throw error;
    }
  },

  /**
   * Sendet eine Nachricht in einer Konversation
   * @param {string} conversationId - ID der Konversation
   * @param {string} message - Nachrichteninhalt
   * @returns {Promise<Object>} - Die gesendete Nachricht
   */
  sendMessage: async (conversationId, message) => {
    try {
      const response = await apiCall(`/api/v1/chat/conversation/${conversationId}/message`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ message }),
      });
      return response;
    } catch (error) {
      console.error('Fehler beim Senden der Nachricht:', error);
      throw error;
    }
  },

  /**
   * Leert den Chatverlauf nur für den aktuellen User (Partner behält ihn).
   * @param {string} conversationId - ID der Konversation
   * @returns {Promise<Object>} - The response from the API.
   */
  clearConversation: async (conversationId) => {
    try {
      const response = await apiCall(`/api/v1/chat/conversation/${conversationId}/clear`, {
        method: 'POST',
      });
      return response;
    } catch (error) {
      console.error('Fehler beim Leeren des Chatverlaufs:', error, { conversationId });
      throw error;
    }
  },

  /**
   * Ends a learning partnership (unmatches a user).
   * @param {string} chatId - The ID of the chat to end.
   * @returns {Promise<Object>} - The response from the API.
   */
  unmatchUser: async (chatId) => {
    try {
      const response = await apiCall(`/api/v1/chat/${chatId}/unmatch`, {
        method: 'DELETE',
      });
      return response;
    } catch (error) {
      console.error('Fehler beim Beenden der Lernpartnerschaft:', error, { chatId });
      // The error object from apiCall might already be structured.
      // If apiCall throws an error that's already suitable for the UI, re-throw it.
      // Otherwise, consider wrapping it or returning a specific error structure.
      // For consistency with other methods in this file, re-throwing is the current pattern.
      throw error;
    }
  },

  /**
   * Prüft den Vernetzungs-Status mit einem anderen User
   * @param {string} targetUserId - ID des anderen Users
   * @returns {Promise<Object>} - { status: 'connected'|'pending_sent'|'pending_received'|'none', chatId?, requestId? }
   *
   * 🆕 Hinzugefügt: 22. Jan 2026 - Für ProfileCard Button-Status
   */
  getConnectionStatus: async (targetUserId) => {
    try {
      // 🔥 PERFORMANCE: Check Cache first
      const cached = connectionStatusCache.get(targetUserId);
      if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
        return cached.data;
      }

      const response = await apiCall(`/api/v1/chat/connection-status/${targetUserId}`);

      // Cache the response
      connectionStatusCache.set(targetUserId, {
        data: response,
        timestamp: Date.now()
      });

      return response;
    } catch (error) {
      console.error('Fehler beim Prüfen des Connection-Status:', error);
      // Bei Fehler: Annehmen dass keine Verbindung besteht
      return { success: true, status: 'none' };
    }
  },

  /**
   * Batch-Abfrage: Connection-Status fuer mehrere User gleichzeitig
   * Ersetzt N einzelne getConnectionStatus() Calls durch 1 API-Call
   * @param {string[]} targetUserIds - Array von User-IDs
   * @returns {Promise<Object>} - { [userId]: { status, chatId, requestId } }
   */
  getConnectionStatusBatch: async (targetUserIds) => {
    try {
      if (!targetUserIds || targetUserIds.length === 0) return {};

      const response = await apiCall('/api/v1/chat/connection-status/batch', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ targetUserIds }),
      });

      // Cache alle Ergebnisse fuer spaetere Einzel-Abfragen
      if (response.statuses) {
        Object.entries(response.statuses).forEach(([uid, data]) => {
          connectionStatusCache.set(uid, {
            data: { success: true, ...data },
            timestamp: Date.now(),
          });
        });
      }

      return response.statuses || {};
    } catch (error) {
      console.error('Fehler beim Batch Connection-Status:', error);
      return {};
    }
  },

  /**
   * Invalidiert den Cache für einen bestimmten User
   * Sollte aufgerufen werden wenn eine Connection-Anfrage gesendet/akzeptiert wird
   */
  invalidateConnectionCache: (targetUserId) => {
    if (targetUserId) {
      connectionStatusCache.delete(targetUserId);
    } else {
      connectionStatusCache.clear();
    }
  },

  /**
   * Markiert eine Konversation als gelesen
   * @param {string} conversationId - ID der Konversation
   * @returns {Promise<Object>} - { success: true }
   */
  markConversationAsRead: async (conversationId) => {
    try {
      const response = await apiCall(`/api/v1/chat/conversation/${conversationId}/read`, {
        method: 'POST',
      });
      return response;
    } catch (error) {
      console.error('Fehler beim Markieren als gelesen:', error);
      throw error;
    }
  },

  /**
   * Meldet eine Chat-Nachricht an Administratoren
   * @param {string} messageId - ID der Nachricht (Datenbank-UUID)
   * @param {Object} reportData - { reason, details, chatId, reportedUserId }
   * @returns {Promise<Object>} - Ergebnis der Meldung
   *
   * 🆕 Hinzugefügt: 3. Feb 2026 - Spam-Meldungen ans Backend senden
   */
  reportMessage: async (messageId, reportData) => {
    try {
      const response = await apiCall(`/api/v1/chat/messages/${messageId}/report`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(reportData),
      });
      return response;
    } catch (error) {
      console.error('Fehler beim Melden der Nachricht:', error);
      throw error;
    }
  }
};

export default ChatService;